import React from 'react';

import { useCompleteModal } from 'modal.react.js';
import { omit } from 'ramda';

import { IRoomOption } from 'components/Pages/Admin/Itinerary/Details/RoomsSelect/types';
import type { TTripEvent } from 'detailsv2/structure';
import { useEvent } from 'hooks/useEvent';

import { useContentSubmitHandlers } from '../TripEventModalContents/shared';
import { usePropertyData, useWizard } from './hooks';
import { AccomodationModalProps } from './types';
import * as types from './types';

export const accomodationWizardContext = React.createContext<types.TAccomodationWizardContext>(
  {} as types.TAccomodationWizardContext,
);

export const AccomodationWizardContextProvider = ({
  children,
  tripEvent,
}: React.PropsWithChildren<AccomodationModalProps>) => {
  const [detailsFormProps, setDetailsFormProps] = React.useState<types.TAccomodationWizardContext['detailsFormProps']>(
    {},
  );
  const [isSuggestedArea, setIsSuggestedArea] = React.useState(true);
  const [createdRooms, setCreatedRooms] = React.useState<IRoomOption[]>([]);

  const completeModal = useCompleteModal<TTripEvent[] | false>();

  const wizard = useWizard<types.TAccomodationWizardData>(['default', 'details', 'page', 'suggested-area'], {
    default: tripEvent,
    details: undefined,
    page: undefined,
    'suggested-area': undefined,
  });

  React.useEffect(() => {
    // reset data on 'details' and 'page' after navigate on default scene
    if (wizard.step === 'default') {
      wizard.updateData('details', undefined);
      wizard.updateData('page', undefined);
    }
  }, [wizard.step]);

  const selectedPropertyId = wizard.data.default.attachedPages?.[0]?.id;
  const { activeAreaPage } = usePropertyData({ pageId: selectedPropertyId });
  const accommodation = wizard.data.default;

  const submitData = useEvent(() => {
    /* remove unexpected backend data */
    const filteredAccommodation = omit(['prevAttachedPage', 'attachedPage'], accommodation);

    const suggestedArea = wizard.data['suggested-area'];
    if (!suggestedArea) return completeModal.success([filteredAccommodation]);

    completeModal.success(
      [filteredAccommodation, ...suggestedArea].map((event, index) => ({ ...event, positions: [index] })),
    );
  });

  const { handleDetailsSubmit, handlePageSubmit } = useContentSubmitHandlers({ wizard, initialValues: accommodation });

  return (
    <accomodationWizardContext.Provider
      value={{
        ...wizard,

        tripEvent,
        submitData,
        activeAreaPage,

        isSuggestedArea,
        setIsSuggestedArea,

        createdRooms,
        setCreatedRooms,

        handleDetailsSubmit,
        handlePageSubmit,

        detailsFormProps,
        setDetailsFormProps,
      }}
    >
      {children}
    </accomodationWizardContext.Provider>
  );
};

export const useAccomodationWizardContext = () => React.useContext(accomodationWizardContext);

export const withWizardContext = (WrappedComponent: React.ComponentType) => {
  return function (props: AccomodationModalProps) {
    return (
      <AccomodationWizardContextProvider {...props}>
        <WrappedComponent />
      </AccomodationWizardContextProvider>
    );
  };
};
